import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useDateRange } from "@/hooks/useDateRange";
import { Loader2, TrendingUp, Lightbulb, ChevronRight } from "lucide-react";

const ROUTE_MAP = {
  rfid: "/rfid",
  gps: "/gps",
  road_sweeping: "/road-sweeping",
  legacy_waste: "/legacy-waste",
  mhari_sadak: "/mhari-sadak",
  swacchata_app: "/swacchata-app",
  jan_samwad: "/jan-samwad",
  sbm_fund: "/sbm-fund",
  property_tax: "/property-tax",
  own_fund: "/own-fund",
  rts: "/rts-timelines",
  area_sabha: "/area-sabha",
  best_practices: "/best-practices",
};

const pctColor = (p) => {
  if (p == null) return "text-slate-400";
  if (p >= 75) return "text-emerald-600";
  if (p >= 50) return "text-amber-600";
  return "text-red-500";
};

export default function MonitoringOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict] = useState("");

  const { params: dateParams, controls: rangeControls } = useDateRange("overview-range");

  useEffect(() => {
    setLoading(true);
    api.get("/monitoring/overview", { params: { ...(district ? { district } : {}), ...dateParams } })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [district, dateParams]);

  useEffect(() => {
    api.get("/municipalities").then(({ data }) => {
      const uniq = [...new Set(data.map((m) => m.district))].sort((a, b) => a.localeCompare(b));
      setDistricts(uniq.map((d) => ({ label: d, value: d })));
    }).catch(() => {});
  }, []);

  const onDistrict = (d) => setDistrict(d);

  const districtOptions = useMemo(() => [{ label: "All Districts", value: "" }, ...districts], [districts]);

  return (
    <div data-testid="monitoring-overview-page">
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-600 font-medium">Dashboard</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">Monitoring Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Key metrics across all monitoring modules{district ? ` · ${district}` : " · statewide"}.</p>
        </div>
        <div className="flex items-end gap-3 flex-wrap" data-testid="overview-filters">
          {rangeControls}
          <div className="w-64" data-testid="overview-district-filter">
            <SearchableSelect value={district} onChange={onDistrict} options={districtOptions} placeholder="Filter by district" testId="overview-district" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.map((m) => (
            <Card
              key={m.key}
              onClick={() => navigate(ROUTE_MAP[m.key])}
              className="group border-slate-200 shadow-sm p-5 flex flex-col justify-between min-h-[140px] cursor-pointer transition-all hover:shadow-md hover:border-emerald-300"
              data-testid={`overview-card-${m.key}`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-700 leading-snug">{m.label}</h3>
                {m.type === "status" ? <Lightbulb className="h-4 w-4 text-emerald-500 shrink-0" /> : <TrendingUp className="h-4 w-4 text-emerald-500 shrink-0" />}
              </div>
              {m.type === "percentage" ? (
                <div className="mt-3">
                  <div className={`text-3xl font-bold tabular-nums ${pctColor(m.avg_percentage)}`}>
                    {m.avg_percentage != null ? `${m.avg_percentage}%` : "—"}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Avg. across {m.count} record(s)</p>
                </div>
              ) : (
                <div className="mt-3">
                  <div className="text-3xl font-bold tabular-nums text-emerald-600">{m.completed}<span className="text-lg text-slate-400">/{m.count}</span></div>
                  <p className="text-xs text-slate-500 mt-1">Completed · {m.in_progress} in progress</p>
                </div>
              )}
              <div className="mt-3 flex items-center text-xs font-medium text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity">
                View details <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
